// packages/core/src/learning/schools/tax-optimization.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. (Worldview named no figures.)
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const TAX_OPTIMIZATION_EXHIBIT: SchoolExhibitEntry = {
	id: 'tax-optimization',
	tagline: 'Tax optimization believes the quiet line item still counts.',
	worldviewSummary:
		'Tax optimization is paying attention to how timing, account choices, income type, deductions, withholding, and record keeping change what money actually becomes after taxes. This lens protects you from ignoring taxes until filing season, confusing gross income with real income, and missing how small planning choices can affect flexibility. It accepts a real tradeoff: tax awareness can improve decisions, but chasing tax moves without understanding the full picture can create complexity, delay, and false confidence. The blind spot is that taxes are personal, rules change, and the smartest move on paper may not fit someone’s actual life, cash flow, or risk.',
	coreBeliefs: [
		'Tax optimization believes after tax money is the money that actually funds your life.',
		'Tax optimization believes timing matters because the same dollar can be treated differently in different situations.',
		'Tax optimization believes records are part of the money system, not filing season cleanup.',
		'Tax optimization believes tax planning should support better decisions, not create complexity for its own sake.',
		'Tax optimization believes deductions, credits, accounts, and withholding should be understood before they are treated like magic.',
		'Tax optimization believes tax awareness is useful only when it stays inside the law and respects uncertainty.',
	],
	warnsAgainst: [
		'Confusing gross income with real spending power.',
		'Waiting until filing season to think about taxes, records, withholding, or income changes.',
		'Making a money move only because it sounds tax smart without understanding the tradeoff.',
		'Treating tax rules as simple, permanent, or the same for every person.',
	],
	helps: 'This lens helps adults who want to understand how taxes affect income, investing, work choices, side income, retirement accounts, and major life decisions. It may not fit when someone wants shortcuts, loophole hunting, or specific tax advice instead of general education and better questions.',
	figures: [],
	canon: [
		{
			title: 'After tax thinking',
			oneLineIdea:
				'A dollar earned and a dollar kept are not always the same thing.',
		},
		{
			title: 'Withholding awareness',
			oneLineIdea:
				'Paychecks can hide tax reality if you never check what is being withheld and why.',
		},
		{
			title: 'Tax advantaged accounts',
			oneLineIdea:
				'Some accounts change when taxes are paid, which can affect flexibility and future options.',
		},
		{
			title: 'Record keeping as protection',
			oneLineIdea:
				'Clean records make tax season less chaotic and help you understand what actually happened.',
		},
		{
			title: 'Do not let the tax tail wag the dog',
			oneLineIdea:
				'A tax benefit is only one part of a decision, not the whole decision.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-tax-optimization',
	debateIds: [],
	applyActions: [
		{
			label: 'Look at your real money picture after taxes and bills',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Test how a tax surprise changes your future options',
			action: {
				type: 'openSimulator',
				label: 'Simulate the tax surprise',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Build a freedom plan that respects after tax reality',
			action: {
				type: 'buildFreedomPlan',
				label: 'Build your plan',
			},
		},
		{
			label: 'Write the tax questions your next money move creates',
			action: {
				type: 'noToolAction',
				label: 'List your tax questions',
				prompt: 'Write the income, account, record, and timing questions you would want answered before making this move.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Your salary is not your spending power. Taxes get a vote.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost is thinking about taxes only after the year is over.',
			kind: 'hook',
		},
		{
			line: 'What tax mistake made you realize gross income and real income are different?',
			kind: 'interview-question',
		},
		{
			line: 'What money move sounded smart until the tax details made it more complicated?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps people understand taxes as tradeoffs, not secret tricks.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Financial education should teach what money becomes after taxes, not just what you earn.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience why a raise, side gig, or account choice can look different after taxes.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask people what they wish they tracked before tax season arrived.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
